<template>
  <div class="flex items-center justify-end">
    <div class="px-2 py-1 text-white border rounded bg-primary">
      {{ items.length }} {{ t('general.records', items.length) }}
    </div>
    <font-awesome-icon class="w-6 h-6 p-1 mx-1 cursor-pointer hover:text-primary-500" icon="fa-light fa-refresh" @click="loadData" />
  </div>
  <div class="relative w-full overflow-y-auto flex-grow">
    <loader v-if="loading" />
    <div v-else class="flex flex-col grid-cols-18 md:grid">
      <div
        v-for="(item, index) in items" :key="index"
        class="flex md:contents border cursor-pointer hover:border-primary-500"
        @click="onItemClick(item)"
      >
        <div class="relative col-start-1 col-end-3 md:mx-auto">
          <div class="flex items-center justify-center w-6 h-full">
            <div class="w-1 h-full pointer-events-none bg-primary" />
          </div>
          <div class="absolute w-6 h-6 -mt-3 text-center rounded-full shadow bg-primary top-1/2">
            <i class="text-white fas fa-check-circle" />
          </div>
        </div>
        <div class="w-full col-start-3 p-4 my-4 mr-auto shadow-md col-end-18 bg-grey-light rounded-xl">
          <div>
            <ul class="mb-0.5 text-base">
              <li class="flex">
                <div class="grow flex flex-col">
                  <span class="text-lg" v-text="`${item.userFriendlyType || item.type} ${item.action}`" />
                  <span v-if="item.subType" class="font-bold" v-text="item.subType" />
                </div>
                <tx-button
                  v-if="item.action === 'deleted'" v-tooltip="t('general.restore')" :disabled="props.permission !== whiteboardConstants.userPermissions.write"
                  type="icon" faicon="fa-light fa-rotate-left" @click="onRestoreObject(item)"
                />
              </li>
              <template v-if="item.diff">
                <li v-for="(d, k) in item.diff" :key="k">
                  <span class="font-semibold">{{ k }}</span>
                  <span class="px-1 mx-1 line-through break-words bg-grey">{{ d.from }}</span>
                  <span class="px-1 mx-1 break-words bg-green-400">{{ d.to }}</span>
                </li>
              </template>
            </ul>
          </div>
          <div class="flex justify-between w-full pt-2 text-sm">
            <div v-tooltip="formatDateTime(item.timestamp)" class="leading-tight text-justify">
              <use-time-ago v-slot="{ timeAgo }" :time="new Date(item.timestamp)">
                {{ timeAgo }}
              </use-time-ago>
            </div>
            <div class="font-medium leading-tight">
              {{ item.userId }}
            </div>
          </div>
        </div>
      </div>
      <div v-if="!items.length" class="col-start-1 col-end-12">
        <p>{{ t('general.emptyList') }}</p>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { UseTimeAgo } from '@vueuse/components'
import { capitalize } from 'lodash-es'
import type Whiteboard from '../../services/whiteboard'
import Loader from '@/shared/components/Loader.vue'
import TxButton from '@/shared/components/TxButton.vue'
import type { HistoryItem } from '@/api/whiteboard/model/boardModel'
import { getBoardHistory } from '@/api/whiteboard/boards'
import { whiteboardConstants } from '@/models/constants'
import { formatDateTime } from '@/services/utils'

const props = defineProps<{
  objectId?: string
  whiteboard?: Whiteboard
  permission: WhiteboardUserPermissionType
}>()

const { t } = useI18n()

const loading = ref(false)
const items = ref<HistoryItem[]>([])

function onItemClick(item: HistoryItem) {
  if (props.whiteboard && item.objectId) {
    const objs = props.whiteboard.canvas.getObjects(item.type) as IWbObject[]
    const itemObj = objs.find(x => x.id === item.objectId)
    if (itemObj) {
      props.whiteboard.moveToView(itemObj)
    }
  }
}

function onRestoreObject(item: HistoryItem) {
  if (props.whiteboard && item.od && props.permission === whiteboardConstants.userPermissions.write) {
    props.whiteboard.addObjectsFromJson([item.od], false, true)
      .then((objs) => {
        if (objs.length) {
          props.whiteboard?.moveToView(objs[0] as IWbObject)
        }
      })
  }
}

function loadData() {
  items.value = []
  if (props.whiteboard && !loading.value) {
    loading.value = true
    const objByIdMap = (props.whiteboard.canvas.getObjects() as Array<any>).filter(x => x.id)
      .reduce((acu, cur) => (acu[cur.id!] = cur) && acu, {} as Record<string, IWbObject>)
    getBoardHistory(props.whiteboard.boardId)
      .then((res) => {
        if (res && res.data) {
          res.data.forEach((item) => {
            // translate type
            let userFriendlyType = capitalize(item.type.replace('Wb', ''))
            if (item.type === whiteboardConstants.objectTypes.shape) {
              userFriendlyType = capitalize(item.subType || userFriendlyType)
              item.subType = undefined
            }
            else if (item.type === whiteboardConstants.objectTypes.articleImage
              || item.type === whiteboardConstants.objectTypes.articleDetails) {
              item.subType = item.objectId ? objByIdMap[item.objectId]?.article?.ArticleNumber || undefined : undefined
              if (item.type === whiteboardConstants.objectTypes.articleImage) {
                userFriendlyType = t('whiteboard.objectType.articleImage')
              }
              else if (item.type === whiteboardConstants.objectTypes.articleDetails) {
                userFriendlyType = t('whiteboard.objectType.articleDetails')
              }
            }
            else if (item.type === whiteboardConstants.objectTypes.modelImage
              || item.type === whiteboardConstants.objectTypes.modelDetails) {
              item.subType = item.objectId ? objByIdMap[item.objectId]?.article?.ModelNumber || undefined : undefined
              if (item.type === whiteboardConstants.objectTypes.modelImage) {
                userFriendlyType = t('whiteboard.objectType.modelImage')
              }
              else if (item.type === whiteboardConstants.objectTypes.modelDetails) {
                userFriendlyType = t('whiteboard.objectType.modelDetails')
              }
            }
            else if (item.type === whiteboardConstants.objectTypes.discussion) {
              userFriendlyType = t('whiteboard.objectType.discussion')
            }

            if (item.action === 'added') {
              if (item.type === whiteboardConstants.objectTypes.frame) {
                item.subType = item.objectId ? objByIdMap[item.objectId]?.name || undefined : undefined
              }
              else if (item.type === whiteboardConstants.objectTypes.textBox
                || item.type === whiteboardConstants.objectTypes.sticky) {
                item.subType = item.objectId ? objByIdMap[item.objectId]?.text || undefined : undefined
              }
            }
            else if (item.action === 'deleted' && item.od) {
              if (item.type === whiteboardConstants.objectTypes.frame) {
                item.subType = item.od.name
              }
              else if (item.type === whiteboardConstants.objectTypes.textBox
                || item.type === whiteboardConstants.objectTypes.sticky) {
                item.subType = item.od.text
              }
            }
            item.userFriendlyType = userFriendlyType
            items.value.push(item)
          })
        }
      })
      .catch(e => console.error(e))
      .finally(() => {
        loading.value = false
      })
  }
}

defineExpose({
  loadData,
})
</script>
